import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Truck, Receipt, Loader2 } from 'lucide-react';
import { bc } from '../lib/api';

const rs = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;

function ShipForm({ o, onDone }) {
  const [f, setF] = useState({ courier: '', tracking_no: '', tracking_url: '' });
  const [busy, setBusy] = useState(false);
  const save = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await bc.ship(o.id, f); toast.success('Marked as shipped. Customer notified.'); onDone(); }
    catch (err) { const det = err?.response?.data?.detail; toast.error((Array.isArray(det) ? det[0]?.msg : det) || 'Failed'); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={save} className="flex flex-wrap gap-2 mt-2">
      <input data-testid={`ship-courier-${o.id}`} required value={f.courier} onChange={e => setF({ ...f, courier: e.target.value })} placeholder="Courier (DTDC, Delhivery, India Post…)" className="flex-1 min-w-[160px] rounded-lg border border-border px-3 py-1.5 text-sm"/>
      <input data-testid={`ship-tracking-${o.id}`} required value={f.tracking_no} onChange={e => setF({ ...f, tracking_no: e.target.value })} placeholder="Tracking / AWB no." className="w-44 rounded-lg border border-border px-3 py-1.5 text-sm"/>
      <input data-testid={`ship-url-${o.id}`} value={f.tracking_url} onChange={e => setF({ ...f, tracking_url: e.target.value })} placeholder="Tracking link (https://…)" className="w-56 rounded-lg border border-border px-3 py-1.5 text-sm"/>
      <button data-testid={`ship-submit-${o.id}`} disabled={busy} className="btn-primary !py-1.5 text-sm">Mark shipped</button>
    </form>
  );
}

export function NationalOrders() {
  const [items, setItems] = useState(null);
  const load = useCallback(() => bc.sellerOrders().then(d => setItems(d.items)).catch(() => setItems([])), []);
  useEffect(() => { load(); }, [load]);
  if (!items) return <Loader2 className="w-5 h-5 animate-spin"/>;
  return (
    <section className="card-soft p-4 space-y-2" data-testid="national-orders">
      <div className="font-heading font-bold flex items-center gap-2"><Truck className="w-4 h-4"/>Orders to ship ({items.filter(o => !o.shipment && o.status !== 'cancelled').length})</div>
      {items.length === 0 ? <p className="text-sm text-ink/60">No BharatCart orders yet.</p> : items.map(o => (
        <div key={o.id} data-testid={`national-order-${o.id}`} className="border-t border-border pt-2 text-sm">
          <div className="flex flex-wrap gap-2 items-center">
            <b>#{o.id.slice(0, 8)}</b><span>{rs(o.total)}</span>
            <span className="pill">{o.payment_method === 'cod' ? 'Cash on delivery' : o.payment_status === 'paid' ? 'Paid online' : 'Awaiting payment'}</span>
            <span className="capitalize text-ink/60">{o.status.replace(/_/g, ' ')}</span>
          </div>
          <div className="text-xs text-ink/60">{o.items.map(i => `${i.quantity} × ${i.name}`).join(', ')}</div>
          {o.ship_to && <div className="text-xs text-ink/60">Ship to: {o.ship_to.full_name}, {o.ship_to.line1}, {o.ship_to.city}, {o.ship_to.state} {o.ship_to.pincode} · {o.ship_to.phone}</div>}
          {o.shipment ? <div className="text-xs text-[#1B5E20] font-semibold" data-testid={`shipped-${o.id}`}>Shipped via {o.shipment.courier} · {o.shipment.tracking_no}</div>
            : o.status === 'cancelled' ? null
            : o.payment_method === 'razorpay' && o.payment_status !== 'paid' ? <div className="text-xs text-[#E67E22]" data-testid={`awaiting-${o.id}`}>Waiting for the customer's payment before you ship.</div>
            : <ShipForm o={o} onDone={load}/>}
        </div>
      ))}
    </section>
  );
}

export function CommissionStatement() {
  const [d, setD] = useState(null);
  useEffect(() => { bc.statement().then(setD).catch(() => setD(null)); }, []);
  if (!d) return null;
  const t = d.totals;
  return (
    <section className="card-soft p-4 space-y-3" data-testid="commission-statement">
      <div className="font-heading font-bold flex items-center gap-2"><Receipt className="w-4 h-4"/>Commission statement</div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
        {[['Sales', t.sales], ['BharatCart commission', t.commission], ['GST on fee (18%)', t.gst_on_fee], ['Your earnings', t.net]].map(([l, v]) => (
          <div key={l} className="rounded-lg bg-white border border-border p-2"><div className="text-xs text-ink/60">{l}</div><div data-testid={`stmt-${l.split(' ')[0].toLowerCase()}`} className="font-bold">{rs(v)}</div></div>
        ))}
      </div>
      <p className="text-xs text-ink/60">{d.route_linked ? 'Payouts for online orders go straight to your linked Razorpay account (held until delivery).' : 'Your Razorpay payout account is not linked yet. BharatCart records what you are owed and settles it to your bank.'}{t.reversed > 0 && ` Fees reversed for cancelled orders: ${rs(t.reversed)}.`}{t.awaiting_payment > 0 && ` Orders awaiting online payment (not counted yet): ${rs(t.awaiting_payment)}.`}</p>
      {d.items.map(o => (
        <details key={o.id} className="border-t border-border pt-2 text-sm">
          <summary className="cursor-pointer flex flex-wrap gap-3"><b>#{o.id.slice(0, 8)}</b><span>{rs(o.bc_commission.order_total)}</span><span>Fee {rs(o.bc_commission.commission)}</span><span className="font-semibold">Net {o.reversed ? '— (cancelled)' : o.awaiting_payment ? '— (awaiting payment)' : rs(o.bc_commission.seller_net)}</span>{o.settlement && <span className="text-xs text-ink/60">{o.settlement.status.replace(/_/g, ' ')}</span>}</summary>
          {o.bc_commission.lines.map(l => <div key={l.product_id} className="text-xs text-ink/70 pl-4">{l.name}: {rs(l.line_total)} × {l.rate_pct}% ({l.basis}) = {rs(l.commission)}</div>)}
        </details>
      ))}
    </section>
  );
}
